import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from '../../routing';
import { commandCenterApi } from '../../utils/api';
import { formatIsoDayShort } from '../../utils/dateFormat';
import { ymToSlashYm } from '../../utils/period';
import { DELETION_REASONS } from '../../utils/deletionReasons';
import Modal from '../../components/Modal';
import MultiSelectFilter from '../../components/MultiSelectFilter';
import StatInfo from '../../components/StatInfo';
import Icon from '../../components/Icon';
import ChartTooltip, { useHoverTip } from '../../components/ChartTooltip';
import { useIsMobile } from '../../utils/useIsMobile';
import { LoadState, useRefetchOnFocus } from './parts';
import { tenantUrl, toUrlRid } from '../../utils/tenantPath';
import { Dash, Metric, SortTh, fmt, pct } from './statsParts';
import { DOKKA_DOC_TYPES as ALL_DOC_TYPES } from './modals';
import {
  formatSeconds, formatTokens, sharePct, baselineInsight, supplierInsight, fieldsInsight,
  deletionsInsight, usageInsight, shapeInsight, TARGET_CUT_PCT, MIN_TIMED, MIN_FACET_TIMED,
} from './extractionInsights';

// סטטיסטיקות → איכות קריאה — how well the AI reads documents and how long
// the review takes (lib/ccExtractionStats.js; spec §7, §9).
//
// Two levels (design + data review, 12/10/2026). The weekly read is at the
// top: one hero number (reviewer seconds per decided draft over every entry,
// the stage 3 KPI — owner, 25/10/2026) against its baseline and target, with
// §9's queue-entry median beside it as the guard, three supporting tiles, the one verdict callout, a
// weekly trend, and the supplier table — the actionable one. The other four
// spec blocks (per field, deletions, AI usage, document shape) are collapsed
// `<details>` rows whose visible line IS their one-sentence reading.
//
// Rules that keep it readable: one median everywhere a single time is shown
// (the KPI is the one average, by the owner's definition); integer percentages; a share under ten judged decisions prints as
// k/n, not a %; no delta until both sides have MIN_COMPARE timed decisions;
// one info note per block; one scope line; one tinted callout on the page.
//
// The page's period and the tab's own filters (document types, customer)
// scope every figure except the baseline, which keeps its own window, entry
// and type filter. The window is movable (owner, 12/10/2026) and rides the
// URL (`bfrom`/`bto`) like the other filters.

const SUPPLIER_PAGE = 10;
const FIELD_PAGE = 5;
const DELETIONS_TABLE_MIN = 10;
const FOCUS_REFETCH_MIN_MS = 60_000;
const SHARE_MIN_N = 10;      // below this a share is k/n, not a percentage
const MEDIAN_MIN_N = MIN_TIMED;          // a median needs this many timed decisions
const FACET_SECONDS_MIN_N = MIN_FACET_TIMED;
const DEFAULT_DOC_TYPES = ['חשבונית', 'תעודת משלוח'];
const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

const FIELD_LABELS = {
  supplier:           'ספק',
  supplier_unmatched: 'ספק לא זוהה',
  doc_type:           'סוג מסמך',
  doc_num:            'מספר מסמך',
  date:               'תאריך',
  subtotal:           'לפני מע״מ',
  vat:                'מע״מ',
  total:              'סה״כ',
  lines_edited:       'שורות שנערכו',
  lines_added:        'שורות שנוספו',
  lines_removed:      'שורות שנמחקו',
};

const SOURCE_LABELS = { single: 'העלאה בודדת', bulk: 'העלאה מרובה', ingester: 'תיקייה אוטומטית', unknown: 'לא ידוע' };
const REASON_LABELS = Object.fromEntries(DELETION_REASONS.map(r => [r.key, r.label]));
REASON_LABELS.unknown = 'ללא סיבה';

const SHAPE_LABELS = {
  pdf: 'PDF', image: 'תמונה', mixed: 'מעורב',
  one_page: 'עמוד אחד', multi_page: 'יותר מעמוד',
  rules: 'עם כללי קריאה', no_rules: 'ללא כללי קריאה',
  single_attempt: 'קריאה אחת', retried: 'קריאה חוזרת',
};
const SHAPE_FACETS = [['pdf', 'image', 'mixed'], ['one_page', 'multi_page'], ['rules', 'no_rules'], ['single_attempt', 'retried']];

export { formatSeconds, sharePct };
const Secs = ({ s }) => (s == null ? <Dash /> : <>{formatSeconds(s)}</>);

// A share with an honest floor: under SHARE_MIN_N judged decisions the
// percentage is false precision, so it prints as k/n in muted ink.
function Share({ n, of }) {
  if (!of) return <Dash />;
  if (of < SHARE_MIN_N) return <span className="cc-muted">{fmt(n)}/{fmt(of)}</span>;
  return <>{sharePct(n, of)}</>;
}

// ─── shared: section head, details row, help copy ───────────────────────────

// Every open block starts the same way: title, one info note, and the block's
// one-sentence reading as its subtitle (plain, not a banner — it is a reading
// of the table, not a problem). Controls at the far end.
function SectionHead({ title, help, reading, children }) {
  return (
    <div className="cc-card-head cc-xq-head">
      <div className="cc-xq-titles">
        <h2 className="cc-card-title">
          {title}
          {help && <StatInfo label={title} ariaLabel={`מה זה ${title}`} maxWidth={320}>{help}</StatInfo>}
        </h2>
        {reading && <p className="cc-xq-reading">{reading}</p>}
      </div>
      <span className="cc-spacer" />
      {children}
    </div>
  );
}

// A collapsed block: the summary line carries the title and its reading, so
// the weekly reader gets the finding without opening anything.
function DetailsBlock({ title, help, reading, children }) {
  return (
    <details className="cc-xq-details">
      <summary className="cc-xq-summary">
        <span className="cc-xq-summary-title">{title}</span>
        <span className="cc-xq-summary-reading">{reading || 'אין מספיק נתונים בטווח'}</span>
        {/* The disclosure chevron at the inline END (the left, in Hebrew) —
            a chevron before the title read as a bullet (owner, 12/10/2026). */}
        <Icon name="chevron" size={14} />
      </summary>
      <div className="cc-xq-details-body">
        {help && (
          <div className="cc-xq-help-line">
            <StatInfo label={title} ariaLabel={`מה זה ${title}`} maxWidth={320}>{help}</StatInfo>
            <span className="cc-muted">מה זה מודד</span>
          </div>
        )}
        {children}
      </div>
    </details>
  );
}

const HELP = {
  gate: (
    <>
      <p><b>צבע הטיוטה</b> — הבדיקה האוטומטית צובעת כל חשבונית ותעודת משלוח בטיוטה: ירוק רק כשכל הבדיקות רצו ועברו, צהוב לבדיקה, אדום לתיקון. בשבוע הצל הצבע מחושב ונשמר אבל לא מוצג לסוקרים.</p>
      <p>נספרות רק החלטות שהטיוטה נפתחה בהן אחת-אחת (אשר מהתור, אשר הכל וסטטוס לא נספרים; כפילים שהמערכת הפרידה, מחיקות ששוחזרו והעלאות ישירות בחוץ). תיקון = מחיקה, לא תקין, או אישור עם שינוי בשדה או בשורה מול מה שהוצג. לא נמדד נספר כתיקון.</p>
      <p>הכותרת: גרסת הבדיקה הנוכחית, 21 הימים האחרונים מאז שהופעלה. הרף לשלב 4: 21 יום, לפחות 500 ירוקות, ופחות מ-1% תוקנו.</p>
    </>
  ),
  gateReasons: 'לכל סיבה בצהוב ובאדום: כמה מהטיוטות שסומנו בה דרשו תיקון. סיבה שכמעט אף פעם לא מנבאת תיקון היא מועמדת להסרה או להקלה.',
  gateShown: 'ירוקות שהצבע שלהן לא הוצג מול ירוקות שהוצג. ההשוואה מראה דילוג של סוקרים לפני ששיעור התיקון מראה אותו.',
  hero: (
    <>
      <p><b>זמן סקירה לטיוטה</b> — סך שניות הסוקרים ÷ מספר הטיוטות שהוכרעו: אישור, לא תקין או מחיקת טיוטה בידי אדם (מחיקה ששוחזרה, כפיל שהמערכת הפרידה ומחיקה של מסמך שכבר אושר לא נספרים). מכל הכניסות — עריכה מהתור, אשר והבא, אשר מהתור, "אשר הכל", פתיחה ישירה ומסך ההעלאה — כך שאישור מרוכז מוריד את המספר כמו שהוא חוסך זמן באמת.</p>
      <p>אשר מהתור ו"אשר הכל" נמדדים מטעינת התור או מההחלטה הקודמת בו; זמן "אשר הכל" מתחלק שווה בין הטיוטות שאושרו. החלטה ישנה בלי זמן נספרת כ-0 שניות ומופיעה כ"בלי זמן", כדי שפער ייראה.</p>
      <p><b>חציון מהתור</b> — החציון על טיוטות שנפתחו מהתור (עריכה / אשר והבא), המדד הקודם. הוא השומר: אם המספר הגדול יורד והחציון עולה, הטיוטות הקשות נעשות איטיות יותר.</p>
      <p><b>קו הבסיס</b> — 11–13/10/26, המסך הישן, על חשבוניות ותעודות משלוח, בכל הלקוחות, <b>בלי קשר לסינון בעמוד</b>. אפשר להזיז את החלון בעיפרון. <b>היעד</b> — {TARGET_CUT_PCT}% פחות מקו הבסיס.</p>
      <p>השוואה מוצגת רק כשלשני הצדדים יש לפחות 30 החלטות, ורק כשהסינון בעמוד זהה לזה של קו הבסיס (חשבונית ותעודת משלוח, כל הלקוחות) — אחרת זו לא השוואה אחד-לאחד. "טיוטות לשעת סוקר" הוא 3,600 ÷ המספר הגדול ולכן לא מוצג כמספר נפרד; הקצב מופיע כהחלטות ליום.</p>
    </>
  ),
  tiles: (
    <>
      <p><b>החלטות</b> — כל טיוטה שסוקר הכריע בה: אישור, לא תקין או מחיקה — וגם מחיקה של מסמך שכבר אושר (כל מחיקה דורשת סיבה). שחזור של מחיקה אינו החלטה. הקצב ליום הוא על ימי הלוח בטווח שיש בהם נתונים — מהיום המלא הראשון של היומן ועד היום.</p>
      <p><b>אושרו ללא שינוי</b> — אישורים שבהם הסוקר לא תיקן כלום מול מה שה-AI קרא. גבוה = קריאה מדויקת. מתוך האישורים שיש להם קריאה.</p>
      <p><b>מחיקות</b> — חלק ההחלטות שהיו מחיקה. מחיקה שבוטלה אחר כך משחזור לא נספרת.</p>
    </>
  ),
  trend: (
    <>
      <p>כל נקודה היא שבוע (ראשון–שבת) בטווח שנבחר, באותו סינון כמו המספרים למעלה. בוחרים איזה מדד לצייר; ריחוף על נקודה מראה את כל המדדים של אותו שבוע.</p>
      <p>בזמן הסקירה לטיוטה הקווים המקווקווים הם קו הבסיס והיעד; בחציון — קו הבסיס של החציון. נקודה חלולה — פחות מ-10 במדגם של אותו שבוע, או שבוע חלקי (הטווח או היום חותכים אותו), והמספר לא מייצג שבוע שלם. שבוע בלי החלטות הוא רווח בקו.</p>
    </>
  ),
  suppliers: (
    <>
      <p>ספק אחד בכל הלקוחות (לפי שם מנורמל), כמו בטאב מסמכים.</p>
      <p><b>תיקונים</b> — החלטות שבהן הסוקר תיקן משהו מול הקריאה, במספר מוחלט. זה סדר המיון: ספק עם 85% בלי שינוי ו-200 מסמכים עולה יותר זמן מספק עם 60% ו-6 מסמכים. <b>ללא שינוי</b> — חלק ההחלטות שעברו בלי תיקון, מתוך אלה שיש להן קריאה. <b>חציון זמן</b> — על הסקירות עם זמן של הספק.</p>
      <p>לחיצה על שורה מראה איזה שדה בדיוק מתוקן אצלו, כותרת מול שורות, ובכמה לקוחות.</p>
    </>
  ),
  fields: (
    <>
      <p>על כל החלטה עם קריאה: איזה שדה הסוקר תיקן מול מה שה-AI קרא.</p>
      <p>שדה שמתוקן הרבה הוא בעיה בקריאה (או בהנחיה ל-AI), לא בסוקר. "ספק לא זוהה" — ה-AI לא הצליח להתאים ספק בכלל, והסוקר בחר אחד.</p>
    </>
  ),
  deletions: (
    <>
      <p>כל מחיקה דורשת סיבה. <b>כפול</b> — המסמך כבר קיים. <b>לא שייך למסעדה הזו</b> — הועלה ללקוח הלא נכון. <b>סריקה לא קריאה</b>. <b>אחר</b> — עם הערה.</p>
      <p>דרך ההעלאה אומרת מאיפה מגיעות המחיקות. מסמך שכבר נוקה סופית מופיע כ"לא ידוע". הרבה "כפול" מהעלאה מרובה = כדאי לעצור כפילויות בזמן ההעלאה.</p>
      <p>עותק שני של מסמך (אותו קובץ, או אותו ספק, סוג, מספר, תאריך וסכום) שהגיע מהקולט או מהעלאה מרובה נמחק אוטומטית. הוא נספר בנפרד ולא נכנס למחיקות של הסוקרים.</p>
    </>
  ),
  usage: (
    <>
      <p>כל קריאת AI שבוצעה — כולל העלאות שהמשתמש נטש ולא שמר ("נשמרו" סופר רק את אלה שהפכו למסמך). <b>נכשלו</b> — קריאות שהמודל לא החזיר מהן תשובה קריאה. הבלוק הזה נספר לפי <b>תאריך ההעלאה</b> ועל <b>כל סוגי המסמכים</b> — לקריאה עוד אין סוג; סינון הלקוח כן חל.</p>
      <p><b>טוקנים</b> — יחידות הטקסט שנשלחו למודל וחזרו ממנו; זה מה שקובע את העלות. <b>מטמון</b> מופיע רק כשיש קריאות ממטמון. <b>מודל</b> מופיע רק כשיותר ממודל אחד פעל בטווח — אחרי החלפת מודל החודש מתפצל לשורה לכל מודל, להשוואה.</p>
      <p>ללא מחירים בכוונה — הם משתנים ונמצאים מחוץ למערכת.</p>
    </>
  ),
  shape: (
    <>
      <p>מה מאפיין את המסמכים שנסקרו, ולצד כל מאפיין — חציון זמן הסקירה שלו. כך רואים אם PDF קשה יותר מתמונה, אם רב-עמודים לוקח יותר, אם כללי קריאה באמת מקצרים.</p>
      <p>זמן מוצג רק למאפיין עם 30 החלטות לפחות. מסמך בלי מידע על עמודים לא נספר באף אחד מהזוגות.</p>
    </>
  ),
};

// ─── KPI strip ──────────────────────────────────────────────────────────────

// The baseline window lives on the hero's own line: a pencil opens the two
// inputs, a reset appears only once the window has moved.
function BaselineWindow({ baseline, onWindow }) {
  const [editing, setEditing] = useState(false);
  const isDefault = baseline.from === baseline.default_from && baseline.to === baseline.default_to;
  if (!editing) {
    return (
      <button type="button" className="cc-kpi-edit" aria-label="שינוי חלון קו הבסיס" onClick={() => setEditing(true)}>
        <Icon name="pencil" size={13} />
      </button>
    );
  }
  return (
    <span className="cc-kpi-window">
      <label className="cc-kpi-date">
        מתאריך
        <input className="form-control" type="date" value={baseline.from} aria-label="תחילת חלון קו הבסיס"
          onChange={e => onWindow(e.target.value, baseline.to)} />
      </label>
      <label className="cc-kpi-date">
        עד
        <input className="form-control" type="date" value={baseline.to} aria-label="סוף חלון קו הבסיס"
          onChange={e => onWindow(baseline.from, e.target.value)} />
      </label>
      {!isDefault && (
        <button type="button" className="btn btn-sm btn-secondary" onClick={() => onWindow('', '')}>ברירת מחדל</button>
      )}
      <button type="button" className="btn btn-sm btn-secondary" onClick={() => setEditing(false)}>סגור</button>
    </span>
  );
}

// Baseline → target as a track; the fill is how far the current KPI has
// come. Nothing is drawn until both sides are comparable.
function TargetBar({ baseline, current }) {
  const base = baseline.kpi.seconds_per_decision;
  if (!baseline.kpi.comparable || current == null || base == null || baseline.target_seconds == null) return null;
  // `current` is passed only when the range's KPI is comparable AND the page
  // filters match the baseline's (Hero: `likeForLike`).
  const span = base - baseline.target_seconds;
  const progress = span > 0 ? Math.max(0, Math.min(1, (base - current) / span)) : 0;
  return (
    <div className="cc-target" aria-hidden="true">
      <span className="cc-target-track"><span className="cc-target-fill" style={{ width: `${Math.round(progress * 100)}%` }} /></span>
    </div>
  );
}

// The hero is the stage 3 KPI (reviewer seconds per decided draft, every
// entry); §9's queue-entry median rides in its detail line as the guard.
function Hero({ h, baseline, onWindow }) {
  const k = h.kpi;
  const bk = baseline.kpi;
  const likeForLike = k.comparable && h.queue.matches_baseline;
  const windowLabel = `${formatIsoDayShort(baseline.from)}–${formatIsoDayShort(baseline.to)}`;
  const baselineLine = bk.comparable
    ? <>קו בסיס <b>{formatSeconds(bk.seconds_per_decision)}</b> · יעד <b>{formatSeconds(baseline.target_seconds)}</b> <span className="cc-muted">({windowLabel})</span></>
    : <>קו בסיס <span className="cc-muted">{fmt(bk.decisions)} החלטות · צריך {baseline.min_compare} להשוואה ({windowLabel})</span></>;
  return (
    <div className="cc-xq-hero">
      <div className="cc-xq-hero-label">
        זמן סקירה לטיוטה
        <StatInfo label="זמן סקירה לטיוטה" ariaLabel="מה זה זמן סקירה לטיוטה" maxWidth={340}>{HELP.hero}</StatInfo>
      </div>
      <div className="cc-xq-hero-value">{formatSeconds(k.seconds_per_decision) ?? '—'}</div>
      <div className="cc-xq-hero-detail">
        {fmt(k.decisions)} החלטות
        {k.untimed > 0 && <> · <span className="cc-muted">{fmt(k.untimed)} בלי זמן (נספרו 0)</span></>}
        {!k.comparable && <> · <span className="cc-muted">צריך {baseline.min_compare} להשוואה</span></>}
        {k.comparable && !h.queue.matches_baseline && <> · <span className="cc-muted">לא בהשוואה ישירה לקו הבסיס — הסינון שונה</span></>}
        {h.queue.median_seconds != null && <> · חציון מהתור: {formatSeconds(h.queue.median_seconds)}</>}
      </div>
      <TargetBar baseline={baseline} current={likeForLike ? k.seconds_per_decision : null} />
      <div className="cc-xq-hero-baseline">
        {baselineLine}
        <BaselineWindow baseline={baseline} onWindow={onWindow} />
        {baseline.complete
          ? <span className="cc-pill ok">החלון נסגר</span>
          : <span className="cc-pill warn">החלון עדיין פתוח</span>}
      </div>
    </div>
  );
}

function Tiles({ h }) {
  return (
    <div className="cc-cardmetrics cc-xq-tiles">
      <Metric
        label={<>החלטות<StatInfo label="החלטות" maxWidth={300}>{HELP.tiles}</StatInfo></>}
        value={fmt(h.decisions)}
        detail={`${h.decisions_per_day} ליום · ${fmt(h.approved)} אושרו · ${fmt(h.invalid)} לא תקינים`}
      />
      <Metric
        label="אושרו ללא שינוי"
        value={h.approved_diffed ? <Share n={h.approved_unchanged} of={h.approved_diffed} /> : '—'}
        detail={`מתוך ${fmt(h.approved_diffed)} אישורים עם קריאה`}
      />
      <Metric
        label="מחיקות"
        value={h.decisions ? <Share n={h.deletions} of={h.decisions} /> : '—'}
        detail={`${fmt(h.deletions)} מההחלטות`}
      />
    </div>
  );
}

// ─── weekly trend widget ────────────────────────────────────────────────────

// The analysis-style trend card (BE העסק's TrendCard anatomy: title, basis
// line, a control at the inline end, the `.cc-line` drawing with the shared
// hover readout) — with a METRIC PICKER, so the same widget answers "is the
// median moving", "is the AI getting more accurate", "are we deleting more"
// (owner, 12/10/2026). Weeks are Israel weeks, Sunday-first; a week under
// SHARE_MIN_N of the metric's own sample draws hollow.
// `rules` names the baseline drawn on the metric: the KPI's baseline and
// target, or the median's own baseline.
const TREND_METRICS = [
  { key: 'kpi', label: 'זמן לטיוטה', value: w => (w.kpi_decisions ? w.seconds_per_decision : null), format: formatSeconds, sample: w => w.kpi_decisions, sampled: true, rules: 'kpi',
    basis: 'שניות הסוקרים לכל טיוטה שהוכרעה בכל שבוע, מכל הכניסות — אותו מדד כמו המספר הגדול. הקווים המקווקווים: קו הבסיס והיעד.' },
  { key: 'median', label: 'חציון מהתור', value: w => (w.timed >= MEDIAN_MIN_N ? w.median_seconds : null), format: formatSeconds, sample: w => w.timed, sampled: true, rules: 'median',
    basis: 'חציון זמן הסקירה בכל שבוע, בכניסה מהתור בלבד — השומר של המספר הגדול. הקו המקווקו: החציון בקו הבסיס.' },
  { key: 'unchanged', label: 'אושרו ללא שינוי', value: w => (w.approved_diffed ? Math.round(w.unchanged_pct) : null), format: v => `${v}%`, sample: w => w.approved_diffed, sampled: true,
    basis: 'חלק האישורים בכל שבוע שעברו בלי תיקון, מתוך האישורים עם קריאה. עולה = ה-AI קורא מדויק יותר.' },
  { key: 'decisions', label: 'החלטות', value: w => w.decisions, format: fmt, sample: w => w.decisions,
    basis: 'כמה טיוטות הוכרעו בכל שבוע — אישור, לא תקין או מחיקה. הקצב, לצד החציון: מהיר יותר אבל פחות החלטות הוא לא שיפור.' },
  { key: 'corrections', label: 'תיקונים', value: w => w.corrections, format: fmt, sample: w => w.diffed,
    basis: 'כמה החלטות בכל שבוע דרשו תיקון מול הקריאה של ה-AI, במספר מוחלט — זמן הסוקרים שהקריאה עלתה.' },
  { key: 'deletions', label: 'מחיקות', value: w => (w.decisions ? Math.round(w.deletions_pct) : null), format: v => `${v}%`, sample: w => w.decisions, sampled: true,
    basis: 'חלק ההחלטות בכל שבוע שהיו מחיקה. מחיקה שבוטלה לא נספרת.' },
  // The draft gate (stage 3): how many of the week's individually reviewed
  // greens needed a fix (unknowns counted as fixes, owner answer 9).
  { key: 'gate_green', label: 'ירוקות שתוקנו', value: w => (w.gate_greens ? w.gate_green_fixed : null), format: fmt, sample: w => w.gate_greens,
    basis: 'כמה מהטיוטות שהבדיקה סימנה בירוק ונבדקו אחת-אחת דרשו תיקון בכל שבוע (כולל לא נמדד). שבוע הצל: הצבע עוד לא מוצג לסוקרים.' },
];

// Every figure of the hovered week, so the readout answers the next question
// without switching metric.
function weekRows(w) {
  return [
    { label: 'זמן לטיוטה', value: w.kpi_decisions ? `${formatSeconds(w.seconds_per_decision)} · ${fmt(w.kpi_decisions)} החלטות${w.untimed ? ` · ${fmt(w.untimed)} בלי זמן` : ''}` : '—' },
    { label: 'חציון מהתור', value: w.median_seconds == null || w.timed < MEDIAN_MIN_N ? `— · ${fmt(w.timed)} עם זמן` : `${formatSeconds(w.median_seconds)} · ${fmt(w.timed)} עם זמן` },
    { label: 'אושרו ללא שינוי', value: w.approved_diffed ? `${Math.round(w.unchanged_pct)}% מתוך ${fmt(w.approved_diffed)}` : '—' },
    { label: 'החלטות', value: `${fmt(w.decisions)} · ${fmt(w.approved)} אושרו · ${fmt(w.invalid)} לא תקינים` },
    { label: 'תיקונים', value: w.diffed ? `${fmt(w.corrections)} מתוך ${fmt(w.diffed)}` : '—' },
    { label: 'מחיקות', value: w.decisions ? `${fmt(w.deletions)} (${Math.round(w.deletions_pct)}%)` : '—' },
    { label: 'ירוקות שתוקנו', value: w.gate_greens ? `${fmt(w.gate_green_fixed)} מתוך ${fmt(w.gate_greens)}` : '—' },
  ];
}

// The dashed lines a metric is read against — only like for like, and only
// once the baseline side has its sample.
function trendRules(metric, baseline, likeForLike) {
  if (!likeForLike) return [];
  if (metric.rules === 'kpi' && baseline.kpi.comparable) return [
    { v: baseline.kpi.seconds_per_decision, cls: 'cc-xq-rule-baseline', label: 'קו בסיס' },
    { v: baseline.target_seconds, cls: 'cc-xq-rule-target', label: 'יעד' },
  ];
  if (metric.rules === 'median' && baseline.comparable) return [
    { v: baseline.median_seconds, cls: 'cc-xq-rule-baseline', label: 'קו בסיס' },
  ];
  return [];
}

function TrendWidget({ weekly = [], baseline, likeForLike }) {
  const [key, setKey] = useState('kpi');
  const metric = TREND_METRICS.find(m => m.key === key) || TREND_METRICS[0];
  const wrapRef = useRef(null);
  const { tip, show, hide } = useHoverTip(wrapRef, { tipHeight: 150 });
  const isMobile = useIsMobile();
  if (weekly.length < 2) return null;

  const n = weekly.length;
  const values = weekly.map(metric.value);
  const rules = trendRules(metric, baseline, likeForLike).filter(r => r.v != null);
  const max = Math.max(...values.filter(v => v != null), ...rules.map(r => r.v), 1);
  // Column centres, as BE העסק's TrendLine: the dots live in flex columns each
  // 1/n wide, so the path is plotted at the same centres. Columns flow RTL in
  // document order; the SVG does not, so x is mirrored.
  const xOf = (i) => n - (i + 0.5);
  const yOf = (v) => 100 - (v / max) * 92 - 4;
  // A week with no value is a GAP: the path restarts (M) after it rather
  // than bridging the neighbours, as the help text promises.
  let prev = -2;
  const path = weekly
    .map((w, i) => {
      if (values[i] == null) return null;
      const op = i === prev + 1 ? 'L' : 'M';
      prev = i;
      return `${op}${xOf(i)},${yOf(values[i])}`;
    })
    .filter(Boolean)
    .join(' ');
  const active = tip ? weekly[tip.i] : null;
  const dense = n > (isMobile ? 6 : 14);

  return (
    <section className="cc-xq-trendcard">
      {/* A fixed stack, so switching metric moves nothing (owner, 12/10/2026):
          the title alone on its row (the info trigger stays put), the chip row,
          a two-line slot for the metric's explanation, a reserved legend line,
          then the drawing in its own white plot box. */}
      <h2 className="cc-card-title cc-xq-trend-title">
        מגמה שבועית
        <StatInfo label="מגמה שבועית" ariaLabel="מה זה מגמה שבועית" maxWidth={320}>{HELP.trend}</StatInfo>
      </h2>
      <div className="cc-xq-metric-tabs" role="tablist" aria-label="מדד למגמה">
        {TREND_METRICS.map(m => (
          <button key={m.key} type="button" role="tab" aria-selected={m.key === metric.key}
            className={`cc-xq-metric-tab${m.key === metric.key ? ' is-active' : ''}`}
            onClick={() => setKey(m.key)}>{m.label}</button>
        ))}
      </div>
      <p className="cc-card-sub cc-xq-basis" aria-live="polite">{metric.basis}</p>
      <div className="cc-xq-rules-legend">
        {rules.map(r => <span key={r.cls}><i className={`cc-xq-rule-swatch ${r.cls}`} /> {r.label} {formatSeconds(r.v)}</span>)}
      </div>
      <div className="cc-xq-plotbox">
      <div className={`cc-line${dense ? ' is-dense' : ''}`} ref={wrapRef}>
        <div className="cc-line-plot">
          <svg className="cc-line-svg" viewBox={`0 0 ${n} 100`} preserveAspectRatio="none" aria-hidden="true" focusable="false">
            {rules.map(r => (
              <line key={r.cls} x1="0" x2={n} y1={yOf(r.v)} y2={yOf(r.v)} className={r.cls} />
            ))}
            <path d={path} fill="none" stroke="var(--peak-green)" strokeWidth="2"
              strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          </svg>
          <div className="cc-line-cols" role="img"
            aria-label={weekly.map((w, i) => `שבוע ${formatIsoDayShort(w.week_start)}: ${values[i] == null ? '—' : metric.format(values[i])}`).join(', ')}>
            {weekly.map((w, i) => (
              <div key={w.week_start} className="cc-line-col"
                onMouseEnter={isMobile ? undefined : (e) => show(i, e.currentTarget)}
                onMouseLeave={isMobile ? undefined : hide}
                // On a phone there is no hover and dense charts hide the values: a
                // tap opens the readout, a second tap closes it.
                onClick={isMobile ? (e) => (tip?.i === i ? hide() : show(i, e.currentTarget)) : undefined}>
                {values[i] != null && (
                  <span className={`cc-line-dot${tip?.i === i ? ' is-active' : ''}${(metric.sampled && metric.sample(w) < SHARE_MIN_N) || w.partial ? ' is-thin' : ''}`}
                    style={{ top: `${yOf(values[i])}%` }} />
                )}
                <span className="cc-xq-col-value">{values[i] == null ? '—' : metric.format(values[i])}</span>
                <span className="cc-line-label">{(!dense || i % 2 === 0) ? formatIsoDayShort(w.week_start) : ''}</span>
              </div>
            ))}
          </div>
        </div>
        {active && (
          <ChartTooltip tip={tip} title={`שבוע ${formatIsoDayShort(active.week_start)}`}
            titleNote={[active.partial ? 'שבוע חלקי' : null, metric.sampled && metric.sample(active) < SHARE_MIN_N ? 'מדגם קטן' : null].filter(Boolean).join(' · ') || undefined}
            rows={weekRows(active)} />
        )}
      </div>
      </div>
    </section>
  );
}

// ─── per supplier ───────────────────────────────────────────────────────────

const NO_SUPPLIER_LABEL = 'לא זוהה ספק';
const supplierLabel = (s) => (s.no_supplier
  ? <span className="cc-pill bad">{NO_SUPPLIER_LABEL}</span>
  : (s.name.trim() || <span className="cc-muted">ספק ללא שם</span>));

const SUPPLIER_COLS = [
  { col: 'decisions', label: 'החלטות' },
  { col: 'corrections', label: 'תיקונים' },
  { col: 'unchanged_pct', label: 'ללא שינוי' },
  { col: 'median_seconds', label: 'חציון זמן' },
];

function FieldsTable({ rows, diffed, limit }) {
  const [showAll, setShowAll] = useState(false);
  const sorted = useMemo(() => [...rows].sort((a, b) => b.count - a.count), [rows]);
  const nonZero = sorted.filter(r => r.count);
  const visible = showAll || !limit ? sorted : nonZero.slice(0, limit);
  const hidden = sorted.length - visible.length;
  return (
    <>
      <div className="table-wrapper">
        <table className="cc-bytype">
          <thead>
            <tr><th>שדה</th><th>תוקן ב־</th><th style={{ minWidth: 140 }}>מתוך {fmt(diffed)} עם קריאה</th></tr>
          </thead>
          <tbody>
            {visible.map(r => (
              <tr key={r.key}>
                <td style={{ fontWeight: 600 }}>{FIELD_LABELS[r.key] || r.key}</td>
                <td className="cc-num">{r.count ? fmt(r.count) : <Dash />}</td>
                <td>
                  <div className="cc-share">
                    <span className="cc-share-track"><span className="cc-share-fill" style={{ width: `${pct(r.count, diffed)}%` }} /></span>
                    <span className="cc-num cc-muted"><Share n={r.count} of={diffed} /></span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hidden > 0 && (
        <button type="button" className="btn btn-sm btn-secondary" style={{ marginTop: 8 }} onClick={() => setShowAll(true)}>
          עוד {fmt(hidden)} שדות{nonZero.length <= visible.length ? ' ללא תיקון' : ''}
        </button>
      )}
    </>
  );
}

function SupplierModal({ s, onClose }) {
  const rows = Object.entries(s.fields).map(([key, count]) => ({ key, count }));
  return (
    <Modal onClose={onClose} size={560} ariaLabel={`פירוט לפי שדה — ${s.no_supplier ? NO_SUPPLIER_LABEL : s.name}`}>
      <h2 className="cc-card-title" style={{ marginBottom: 4 }}>{supplierLabel(s)}</h2>
      <p className="cc-muted" style={{ marginTop: 0 }}>
        {fmt(s.decisions)} החלטות ב-{fmt(s.restaurants)} לקוחות · {fmt(s.diffed)} עם קריאה · {fmt(s.deletions)} מחיקות
      </p>
      <div className="cc-cardmetrics">
        <Metric label="כותרת שונתה" value={<Share n={s.header_changed} of={s.diffed} />} />
        <Metric label="שורות שונו" value={<Share n={s.lines_changed} of={s.diffed} />} />
        <Metric label="חציון זמן" value={s.timed >= MEDIAN_MIN_N ? (formatSeconds(s.median_seconds) ?? '—') : '—'} detail={`${fmt(s.timed)} סקירות עם זמן`} />
      </div>
      <FieldsTable rows={rows} diffed={s.diffed} />
    </Modal>
  );
}

function SupplierTable({ suppliers }) {
  const [sort, setSort] = useState('corrections');
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(null);
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = needle ? suppliers.filter(s => (s.no_supplier ? NO_SUPPLIER_LABEL : s.name).toLowerCase().includes(needle)) : suppliers;
    // What the cell shows as "—" sorts as missing: a 2-decision 15-minute
    // median must not top the "slowest" column while displaying a dash.
    const shown = (s) => {
      if (sort === 'median_seconds') return s.timed >= MEDIAN_MIN_N ? s.median_seconds : null;
      if (sort === 'unchanged_pct') return s.diffed ? s.unchanged_pct : null;
      return s[sort];
    };
    return [...filtered].sort((a, b) => (shown(b) ?? -1) - (shown(a) ?? -1) || b.corrections - a.corrections || b.decisions - a.decisions);
  }, [suppliers, q, sort]);
  const visible = showAll || q ? rows : rows.slice(0, SUPPLIER_PAGE);

  return (
    <section className="cc-tabpanel-section">
      <SectionHead title="לפי ספק" help={HELP.suppliers} reading={supplierInsight(suppliers)}>
        <input className="form-control" style={{ maxWidth: 220 }} value={q} onChange={e => setQ(e.target.value)} placeholder="חיפוש ספק…" aria-label="חיפוש ספק" />
      </SectionHead>
      {suppliers.length === 0 ? <div className="cc-empty">אין החלטות בטווח</div> : rows.length === 0 ? <div className="cc-empty">אין ספק שתואם לחיפוש</div> : (
        <div className="cc-matrix-scroll">
          <table className="cc-list cc-clickable">
            <thead>
              <tr>
                <th className="cc-matrix-name">ספק</th>
                {SUPPLIER_COLS.map(c => <SortTh key={c.col} col={c.col} sort={sort} onSort={setSort}>{c.label}</SortTh>)}
              </tr>
            </thead>
            <tbody>
              {visible.map(s => (
                <tr key={s.key} onClick={() => setOpen(s)} tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(s); } }}>
                  <td className="cc-matrix-name">{supplierLabel(s)}</td>
                  <td className="cc-num">{fmt(s.decisions)}</td>
                  <td className="cc-num" style={{ fontWeight: 700 }}>{s.corrections ? fmt(s.corrections) : <Dash />}</td>
                  <td className="cc-num"><Share n={s.unchanged} of={s.diffed} /></td>
                  <td className="cc-num">{s.timed >= MEDIAN_MIN_N ? <Secs s={s.median_seconds} /> : <span className="cc-muted">— · {fmt(s.timed)}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!q && !showAll && rows.length > SUPPLIER_PAGE && (
        <button className="btn btn-sm btn-secondary" style={{ marginTop: 10 }} onClick={() => setShowAll(true)}>
          הצג את כל {fmt(rows.length)} הספקים
        </button>
      )}
      {open && <SupplierModal s={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

// ─── צבע הטיוטה: the draft gate (stage 3, #331) ─────────────────────────────

const GATE_COLORS = [
  { key: 'green', label: 'ירוק' },
  { key: 'yellow', label: 'צהוב' },
  { key: 'red', label: 'אדום' },
];
const GATE_REASON_LABELS = {
  supplier: 'ספק', recipient: 'נמען', duplicate: 'כפיל', date: 'תאריך', row_total: 'סך שורה', totals: 'סכומים',
  vat: 'מע״מ', price: 'מחירים', new_product: 'מוצר חדש', delivery_notes: 'תעודות משלוח', not_checked: 'לא נבדק',
};
const GATE_FIX_LABELS = {
  supplier: 'ספק', doc_type: 'סוג', doc_num: 'מספר', date: 'תאריך', subtotal: 'סכום', invoice_discount: 'הנחת חשבונית',
  vat: 'מע״מ', total: 'סה״כ', line_identity: 'מוצר בשורה', line_numbers: 'מספרים בשורה', lines_added: 'שורה נוספה', lines_removed: 'שורה הוסרה',
};
const fixedOrUnknown = (c) => c.fixed + c.unknown;

// The headline counts against the allowance, never a percent: with a 1% bar
// a percent is the wrong unit (spec §3, brain 08 rule 11).
function gateReading(g) {
  const b = g.bar;
  const fixes = b.unknown ? `${fmt(b.fixed)} תוקנו + ${fmt(b.unknown)} לא נמדד` : `${fmt(b.fixed)} תוקנו`;
  if (b.greens < b.min_greens) return `ירוקות בגרסה ${g.version}: ${fixes} מתוך ${fmt(b.greens)} · צריך ${fmt(b.min_greens)} ירוקות לפני שקובעים`;
  return `ירוקות בגרסה ${g.version}: ${fixes} מתוך ${fmt(b.greens)} שנבדקו אחת-אחת · מותר עד ${fmt(b.allowed)} (פחות מ-1%)${b.met ? ' · הרף עבר' : ''}`;
}

function GateFixList({ rows }) {
  if (!rows.length) return <div className="cc-empty">אף ירוקה לא דרשה תיקון בטווח</div>;
  return (
    <div className="cc-matrix-scroll">
      <table className="cc-list">
        <thead><tr><th>תאריך</th><th>ספק</th><th>מה תוקן</th><th>סוקר</th><th /></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.document_id}>
              <td>{formatIsoDayShort(String(r.decided_at).slice(0, 10))}</td>
              <td style={{ fontWeight: 600 }}>{r.supplier_name || <span className="cc-muted">—</span>}</td>
              <td>{r.decision === 'delete' ? 'נמחק' : r.decision === 'invalid' ? 'לא תקין'
                : r.outcome === 'unknown' ? 'לא נמדד'
                  : (r.fix_fields || []).filter(f => !f.startsWith('minor:')).map(f => GATE_FIX_LABELS[f] || f).join(', ')}</td>
              <td className="cc-muted">{r.decided_by || '—'}</td>
              <td>{toUrlRid(r.restaurant_id) != null && (
                <a href={tenantUrl(r.restaurant_id, `/document/${r.document_id}`)} target="_blank" rel="noopener noreferrer">פתח</a>
              )}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GateTable({ table }) {
  const total = GATE_COLORS.reduce((n, c) => n + table[c.key].decided, 0);
  return (
    <div className="cc-matrix-scroll">
      <table className="cc-list">
        <thead><tr><th /><th>ירוק</th><th>צהוב</th><th>אדום</th></tr></thead>
        <tbody>
          <tr><td>הוכרעו</td>{GATE_COLORS.map(c => <td key={c.key} className="cc-num">{fmt(table[c.key].decided)}</td>)}</tr>
          <tr><td>דרשו תיקון</td>{GATE_COLORS.map(c => (
            <td key={c.key} className="cc-num" title={table[c.key].minor ? `${fmt(table[c.key].minor)} עם שינוי קל בלבד (לא נספר)` : undefined}>
              {fmt(fixedOrUnknown(table[c.key]))}/{fmt(table[c.key].decided)}
            </td>
          ))}</tr>
          <tr><td>חלק מההחלטות</td>{GATE_COLORS.map(c => (
            <td key={c.key} className="cc-num">{total ? `${Math.round((table[c.key].decided / total) * 100)}%` : <Dash />}</td>
          ))}</tr>
        </tbody>
      </table>
    </div>
  );
}

function GateBlock({ g }) {
  const b = g.bar;
  const shownLine = (side) => `${fmt(side.fixed)}/${fmt(side.greens)} תוקנו${side.median_seconds != null ? ` · חציון ${formatSeconds(side.median_seconds)}` : ''}`;
  return (
    <section className="cc-tabpanel-section">
      <SectionHead title="צבע הטיוטה" help={HELP.gate} reading={gateReading(g)} />
      <p className="cc-muted" style={{ margin: '0 0 4px' }}>
        גרסה {g.version} פעילה: יום {fmt(Math.min(b.day, b.days_needed))} מתוך {fmt(b.days_needed)}
        {g.coverage.greens_without_view > 0 && ` · עוד ${fmt(g.coverage.greens_without_view)} ירוקות אושרו בלי פתיחה (לא נספרו)`}
      </p>
      <GateFixList rows={g.green_fixes} />
      <GateTable table={g.table} />
      <div className="cc-xq-details-list">
        <DetailsBlock title="שיעור תיקון לפי סיבה" help={HELP.gateReasons}
          reading={g.reasons.length ? `${g.reasons.length} סיבות בצהוב ובאדום` : null}>
          {g.reasons.length === 0 ? <div className="cc-empty">אין טיוטות צהובות או אדומות בטווח</div> : (
            <table className="cc-list">
              <thead><tr><th>סיבה</th><th>הוכרעו</th><th>דרשו תיקון</th></tr></thead>
              <tbody>
                {g.reasons.map(r => (
                  <tr key={r.code}>
                    <td>{GATE_REASON_LABELS[r.code] || r.code}</td>
                    <td className="cc-num">{fmt(r.decided)}</td>
                    <td className="cc-num">{fmt(r.fixed)}/{fmt(r.decided)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DetailsBlock>
        <DetailsBlock title="מוסתר מול מוצג" help={HELP.gateShown}
          reading={`מוסתר: ${shownLine(g.hidden_vs_shown.hidden)} · מוצג: ${shownLine(g.hidden_vs_shown.shown)}`}>
          <p className="cc-muted" style={{ margin: 0 }}>
            ירוקות שהצבע לא הוצג לסוקר (שבוע הצל, ואחר כך 1 מכל 10) מול ירוקות שהצבע הוצג. אם המוצגות מתוקנות פחות או נבדקות מהר בהרבה — הסוקרים מדלגים.
          </p>
        </DetailsBlock>
      </div>
    </section>
  );
}

// ─── collapsed blocks ───────────────────────────────────────────────────────

function DeletionsBlock({ d }) {
  const reasons = Object.keys(REASON_LABELS).filter(k => d.by_reason[k]);
  const sourceLine = Object.entries(d.by_source)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${SOURCE_LABELS[k] || k} ${fmt(n)}`)
    .join(' · ');
  return (
    <DetailsBlock title="מחיקות" help={HELP.deletions} reading={deletionsInsight(d, REASON_LABELS, SOURCE_LABELS)}>
      {d.auto_duplicates > 0 && (
        <p className="cc-muted" style={{ margin: '0 0 8px' }}>
          בנוסף: {fmt(d.auto_duplicates)} עותקים כפולים נמחקו אוטומטית (לא נספרים כאן)
        </p>
      )}
      {/* Drafts moved to the right restaurant instead of deleted as לא שייך
          למסעדה הזו (#304) — beside the deletions, never inside them. */}
      {d.moved != null && (
        <p className="cc-muted" style={{ margin: '0 0 8px' }}>
          הועברו למסעדה אחרת: {fmt(d.moved)} (לא נספרות כמחיקות)
        </p>
      )}
      {d.total === 0 ? <div className="cc-empty">אין מחיקות בטווח</div> : (
        <>
          <p className="cc-muted" style={{ margin: '0 0 8px' }}>לפי דרך ההעלאה: {sourceLine}</p>
          {d.total >= DELETIONS_TABLE_MIN ? (
            <div className="cc-matrix-scroll">
              <table className="cc-list">
                <thead>
                  <tr><th>לקוח</th><th>סה״כ</th>{reasons.map(k => <th key={k}>{REASON_LABELS[k]}</th>)}</tr>
                </thead>
                <tbody>
                  {d.by_restaurant.slice(0, 5).map(r => (
                    <tr key={r.restaurant_id}>
                      <td style={{ fontWeight: 600 }}>{r.name}</td>
                      <td className="cc-num" style={{ fontWeight: 700 }}>{fmt(r.total)}</td>
                      {reasons.map(k => <td key={k} className="cc-num">{r.by_reason[k] ? fmt(r.by_reason[k]) : <Dash />}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="cc-muted" style={{ margin: 0 }}>
              לפי סיבה: {reasons.map(k => `${REASON_LABELS[k]} ${fmt(d.by_reason[k])}`).join(' · ')} · טבלה מ-{DELETIONS_TABLE_MIN} מחיקות
            </p>
          )}
        </>
      )}
    </DetailsBlock>
  );
}

function UsageBlock({ u }) {
  const showModel = (u.models || []).length > 1;
  const showCache = u.rows.some(r => r.cache_read_tokens > 0);
  return (
    <DetailsBlock title="שימוש ב-AI" help={HELP.usage} reading={usageInsight(u)}>
      {u.rows.length === 0 ? <div className="cc-empty">אין קריאות AI בטווח</div> : (
        <div className="cc-matrix-scroll">
          <table className="cc-list">
            <thead>
              <tr>
                <th>חודש</th><th>לקוח</th>{showModel && <th>מודל</th>}
                <th>קריאות</th><th>נשמרו</th><th>נכשלו</th><th>טוקנים</th>{showCache && <th>מטמון</th>}<th>זמן תגובה</th>
              </tr>
            </thead>
            <tbody>
              {u.rows.map(r => (
                <tr key={`${r.restaurant_id}|${r.month}|${r.model}`}>
                  <td className="cc-num">{ymToSlashYm(r.month)}</td>
                  <td style={{ fontWeight: 600 }}>{r.name}</td>
                  {showModel && <td className="cc-xq-model">{r.model}</td>}
                  <td className="cc-num" style={{ fontWeight: 700 }}>{fmt(r.calls)}</td>
                  <td className="cc-num"><Share n={r.claimed} of={r.calls} /></td>
                  <td className="cc-num">{r.parse_failed ? <Share n={r.parse_failed} of={r.calls} /> : <Dash />}</td>
                  <td className="cc-num">{formatTokens(r.input_tokens + r.output_tokens)}</td>
                  {showCache && <td className="cc-num">{r.cache_read_tokens ? formatTokens(r.cache_read_tokens) : <Dash />}</td>}
                  <td className="cc-num">{r.mean_latency_ms == null ? <Dash /> : `${(r.mean_latency_ms / 1000).toFixed(1)} שנ׳`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DetailsBlock>
  );
}

// Each facet is one line: its groups side by side with share and median, so
// the pair reads as a comparison rather than a column that never sums to 100.
function ShapeBlock({ shape }) {
  const byKey = Object.fromEntries(shape.groups.map(g => [g.key, g]));
  return (
    <DetailsBlock title="צורת המסמך" help={HELP.shape} reading={shapeInsight(shape)}>
      {shape.reviewed === 0 ? <div className="cc-empty">אין החלטות עם קריאה בטווח</div> : (
        <>
          <p className="cc-muted" style={{ margin: '0 0 8px' }}>
            {fmt(shape.reviewed)} החלטות עם קריאה · {shape.mean_pages ?? '—'} עמודים בממוצע · {shape.mean_attempts ?? '—'} קריאות בממוצע
          </p>
          <ul className="cc-xq-facets">
            {SHAPE_FACETS.map((facet, i) => (
              <li key={i}>
                {facet.map(key => byKey[key]).filter(g => g && g.n).map(g => (
                  <span key={g.key} className="cc-xq-facet">
                    <b>{SHAPE_LABELS[g.key]}</b> <Share n={g.n} of={shape.reviewed} />
                    <span className="cc-muted"> · {g.timed >= FACET_SECONDS_MIN_N && g.median_seconds != null ? formatSeconds(g.median_seconds) : '—'}</span>
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </>
      )}
    </DetailsBlock>
  );
}

// ─── data ───────────────────────────────────────────────────────────────────

// Same shape as StatsPage's useDocumentStats: the load token drops a response
// that a newer filter change or a focus refetch has superseded.
// Unlike StatsPage's useDocumentStats, a filter change keeps the previous
// payload on screen (dimmed, aria-busy) until the new one lands: nulling it
// would unmount the hero, close the baseline editor mid-edit, and reset the
// metric chip, the supplier sort and every open <details> (review, 12/10).
function useExtractionStats(args) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const loadToken = useRef(0);
  const key = JSON.stringify(args);
  const load = useCallback(async () => {
    const token = ++loadToken.current;
    setError(false);
    setLoading(true);
    try {
      const r = await commandCenterApi.getExtractionStats(JSON.parse(key));
      if (token !== loadToken.current) return;
      setData(r.data);
      setLoading(false);
    } catch {
      if (token !== loadToken.current) return;
      setError(true);
      setData(null);
      setLoading(false);
    }
  }, [key]);
  useEffect(() => { load(); }, [load]);
  // A focus refetch repeats ten statements on the synchronous server; this
  // is a statistics tab, not a queue, so a return within a minute keeps
  // what is on screen (perf review, 12/10/2026).
  const loadedAt = useRef(0);
  useEffect(() => { if (data) loadedAt.current = Date.now(); }, [data]);
  const refetchIfStale = useCallback(() => { if (Date.now() - loadedAt.current > FOCUS_REFETCH_MIN_MS) load(); }, [load]);
  useRefetchOnFocus(refetchIfStale);
  return { data, error, loading, load };
}

function useCustomers() {
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    let alive = true;
    commandCenterApi.getCustomers()
      // Same list as לטיפול's restaurant filter: demo tenants out, as the figures are.
      .then(r => { if (alive) setCustomers((r.data || []).filter(c => !c.is_demo)); })
      // The filter just stays empty; the figures don't depend on it.
      .catch(err => console.error('[extraction] customers', err));
    return () => { alive = false; };
  }, []);
  return customers;
}

// ─── view ───────────────────────────────────────────────────────────────────

export default function ExtractionQualityView({ dateFrom, dateTo }) {
  const [params, setParams] = useSearchParams();
  const docTypes = useMemo(() => (params.get('types') || '').split(',').filter(t => ALL_DOC_TYPES.includes(t)), [params]);
  const restaurantId = /^\d+$/.test(params.get('restaurant') || '') ? params.get('restaurant') : '';
  const baselineFrom = ISO_DAY_RE.test(params.get('bfrom') || '') ? params.get('bfrom') : '';
  const baselineTo = ISO_DAY_RE.test(params.get('bto') || '') ? params.get('bto') : '';
  const customers = useCustomers();

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const { data, error, loading, load } = useExtractionStats({
    from: dateFrom, to: dateTo, docTypes, restaurantId, baselineFrom, baselineTo,
  });

  const onWindow = (from, to) => {
    // A native date input fires onChange per keystroke while a year is typed
    // ("0002-10-11"); only a plausible day reaches the URL. A bound equal to
    // the default stays out of it, so a link with no window means "the §9
    // window" even if the default ever moves.
    const plausible = (d) => !d || (ISO_DAY_RE.test(d) && d >= '2020-01-01' && d <= '2100-12-31');
    if (!plausible(from) || !plausible(to)) return;
    const next = new URLSearchParams(params);
    const { default_from: dFrom, default_to: dTo } = data.baseline;
    if (from && from !== dFrom) next.set('bfrom', from); else next.delete('bfrom');
    if (to && to !== dTo) next.set('bto', to); else next.delete('bto');
    setParams(next, { replace: true });
  };

  const customerName = data?.restaurant_id
    ? (customers.find(c => String(c.id) === String(data.restaurant_id))?.name || 'לקוח אחד')
    : 'כל הלקוחות';

  return (
    <>
      <div className="docs-filter-bar cc-xq-filters">
        <MultiSelectFilter
          label="סוג מסמך"
          values={docTypes.length ? docTypes : DEFAULT_DOC_TYPES}
          options={ALL_DOC_TYPES}
          onChange={(types) => setParam('types', types.join(','))}
          emptyLabel=""
        />
        <select className="form-control" style={{ maxWidth: 220 }} aria-label="לקוח" value={restaurantId}
          onChange={e => setParam('restaurant', e.target.value)}>
          <option value="">כל הלקוחות</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {data && (
          <span className="cc-xq-since">
            נתונים מ-{formatIsoDayShort(data.data_since)}
            <StatInfo label="מאז מתי יש נתונים" ariaLabel="מאז מתי יש נתונים" maxWidth={320}>
              <p>יומן הסקירה ויומן הקריאות של ה-AI נכנסו לייצור בליל {formatIsoDayShort(data.data_since)} (PR #207, שלב 0 של מפת הדרכים — מדידה לפני שינוי). לפני כן המערכת לא שמרה כלום על הסקירה, ולכן טווח שמתחיל מוקדם יותר פשוט ריק.</p>
              <p>מחיקות עם סיבה ושחזור מהקונסול — מ-11/10/26 (PR #215). הטאב הזה — PR #237.</p>
              <p>קו הבסיס של היעד נמדד על 11–13/10/26, המסך הישן.</p>
            </StatInfo>
          </span>
        )}
      </div>

      <LoadState loading={!data && !error} error={error} onRetry={load} empty={false}>
        {data && (
          <div className={`cc-xq-body${loading ? ' is-refetching' : ''}`} aria-busy={loading || undefined}>
            <p className="cc-muted cc-xq-scope">
              {formatIsoDayShort(data.range.from)}–{formatIsoDayShort(data.range.to)} · {data.doc_types.join(', ')} · {customerName}
            </p>
            <div className="cc-xq-strip">
              <Hero h={data.headline} baseline={data.baseline} onWindow={onWindow} />
              <Tiles h={data.headline} />
            </div>
            {data.headline.queue.matches_baseline && baselineInsight(data.baseline, data.headline.kpi) && (
              <p className="cc-insight">{baselineInsight(data.baseline, data.headline.kpi)}</p>
            )}
            <TrendWidget weekly={data.headline.weekly} baseline={data.baseline} likeForLike={data.headline.queue.matches_baseline} />
            <SupplierTable suppliers={data.suppliers} />
            <section className="cc-tabpanel-section">
              <SectionHead title="לפי שדה" help={HELP.fields} reading={fieldsInsight(data.fields, FIELD_LABELS)} />
              <FieldsTable rows={data.fields.rows} diffed={data.fields.diffed} limit={FIELD_PAGE} />
            </section>
            {data.gate && <GateBlock g={data.gate} />}
            <div className="cc-xq-details-list">
              <DeletionsBlock d={data.deletions} />
              <UsageBlock u={data.ai_usage} />
              <ShapeBlock shape={data.shape} />
            </div>
          </div>
        )}
      </LoadState>
    </>
  );
}
